<script setup>
import { ref, computed } from "vue";

const props = defineProps({
    values: { type: Object, required: true }, // { parentKey: [childObj, ...], ... }
    displayParent: { type: Object, required: true }, // { parentKey: displayValue }
    childDisplayKey: { type: String, required: true }, // key in child object for display
    modelValue: { type: Object, default: () => ({}) }, // { parentKey: [childKey, ...] }
    exportChildArray: { type: Boolean, default: false } // NEW PROP
});

const emit = defineEmits([ "update:modelValue" ]);
const selected = ref({});
const search = ref("");

// Unique key for child
function getChildKey(child) {
    return child.ROWID ?? child.id ?? JSON.stringify(child);
}

// Helper to emit correct format
function emitSelected() {
    if (props.exportChildArray) {
        // Flatten all selected child keys into a single array
        const arr = Object.values(selected.value).flat();
        emit("update:modelValue", arr);
    } else {
        emit("update:modelValue", { ...selected.value });
    }
} emitSelected();

// Parent is selected if any child is selected
function isParentSelected(parentKey) {
    return Array.isArray(selected.value[parentKey]) && selected.value[parentKey].length > 0;
}

// Child is selected if its key is in parent's array
function isChildSelected(parentKey, child) {
    const childKey = getChildKey(child);
    return Array.isArray(selected.value[parentKey]) && selected.value[parentKey].includes(childKey);
}

// Toggle all children for parent
function toggleParent(parentKey) {
    const children = props.values[parentKey] || [];
    if (isParentSelected(parentKey)) {
        selected.value[parentKey] = [];
    } else {
        selected.value[parentKey] = children.map(getChildKey);
    }
    emitSelected();
}

// Toggle individual child
function toggleChild(parentKey, child) {
    const childKey = getChildKey(child);
    if (!Array.isArray(selected.value[parentKey])) selected.value[parentKey] = [];
    const idx = selected.value[parentKey].indexOf(childKey);
    if (idx === -1) {
        selected.value[parentKey].push(childKey);
    } else {
        selected.value[parentKey].splice(idx, 1);
    }
    emitSelected();
}

// Filter parents by search (display or key)
const filteredParents = computed(() => {
    const q = search.value.trim().toLowerCase();
    if (!q) return Object.keys(props.values);
    return Object.keys(props.values).filter((parentKey) =>
        (props.displayParent[parentKey] || "").toLowerCase().includes(q) ||
        parentKey.toLowerCase().includes(q)
    );
});
</script>

<template>
  <div>
    <input
      v-model="search"
      type="text"
      placeholder="Search by therapist name or ID..."
      class="mb-2 w-full border border-blue-200 rounded-lg px-2 py-1 focus:ring-blue-500 focus:border-blue-500 text-lg"
    />
    <div class="space-y-2 max-h-52 min-h-52 select-none overflow-y-scroll">
      <div
        v-for="parentKey in filteredParents"
        :key="parentKey"
        class="border p-2 rounded-sm shadow-sm"
      >
        <!-- Parent row -->
        <div class="flex items-center justify-between mb-2">
          <label class="flex items-center cursor-pointer">
            <input
              type="checkbox"
              :checked="isParentSelected(parentKey)"
              @change="toggleParent(parentKey)"
              class="hidden"
            />
            <span
              class="text-xl font-bold text-blue-900"
              :class="isParentSelected(parentKey) ? 'hover:text-red-700' : 'hover:text-green-700'"
            >
              {{ displayParent[parentKey] || parentKey }}
            </span>
          </label>
        </div>
        <!-- Children always visible -->
        <div class="mt-2 grid grid-cols-2 md:grid-cols-3 gap-3">
          <label
            v-for="child in values[parentKey] || []"
            :key="getChildKey(child)"
            class="text-center cursor-pointer border font-semibold border-blue-100 rounded-md px-2 py-1 transition"
            :class="
              isChildSelected(parentKey, child) ?
              'bg-blue-200 hover:bg-red-100 hover:text-red-500' :
              'bg-gray-50 hover:bg-green-100 hover:text-green-500'"
          >
            <input
              type="checkbox"
              :checked="isChildSelected(parentKey, child)"
              @change="toggleChild(parentKey, child)"
              class="hidden"
            />
            <span
              :title="child[childDisplayKey]"
            >
              {{ child[childDisplayKey] }}
            </span>
          </label>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.2s;
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
}
</style>
